import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ApiError, explain } from '@/shared/api/problem'
import { useDebounced } from '@/shared/hooks/useDebounced'
import {
  listParties, searchCandidates, getParty, getCandidate, listCoalitions, getCoalition,
} from '@/features/politics/api'
import { listUsers, EMPTY_USER_FILTERS } from '@/features/users/api'

/**
 * A searchable picker for a foreign id — a party, a candidate, a user.
 *
 * ## Why these fields cannot be text boxes
 *
 * They were, and a uuid typed by hand is three bad outcomes: a typo that fails a foreign
 * key at save time, a *valid* uuid pointing at the wrong record, and — for
 * `perpetratorPartyId` on an incident — an allegation attached to whichever party that
 * string happens to name. Naming a party as the alleged perpetrator of election violence
 * is the highest-consequence write this feature makes; it must be a deliberate selection
 * from a list, showing the name the reader will later see.
 *
 * ## One component, three sources
 *
 * The three lists differ only in which endpoint they query and how a row reads. Three
 * near-identical dropdowns would drift in their debounce, their empty state and their
 * refusal handling within a release.
 *
 * ## A cross-feature grant, stated
 *
 * `/politics/*` needs `politics:read` and `/users` needs `user:read` — different features'
 * authorities that an incident reporter need not hold. A 403 here renders a sentence
 * naming the authority and leaves the field usable by other means, rather than showing an
 * empty dropdown that looks like "there are no parties".
 */

export interface PickerOption {
  id: string
  label: string
  hint?: string | null
}

type Source = 'party' | 'candidate' | 'coalition' | 'user'

const SOURCE_AUTHORITY: Record<Source, string> = {
  party: 'politics:read',
  candidate: 'politics:read',
  coalition: 'politics:read',
  user: 'user:read',
}

async function query(source: Source, q: string): Promise<PickerOption[]> {
  switch (source) {
    case 'party': {
      const page = await listParties({ search: q || undefined }, 0, 25)
      return page.content.map((p) => ({
        id: p.id,
        label: p.shortName ? `${p.name} (${p.shortName})` : p.name,
        hint: p.statusCode,
      }))
    }
    case 'candidate': {
      // The server's own typeahead — scope-filtered there, not here.
      const rows = await searchCandidates(q, undefined, undefined, 25)
      return rows.map((c) => ({
        id: c.id,
        label: c.fullName,
        hint: [c.seatTypeCode, c.unitLabel, c.party?.shortName ?? c.party?.name]
          .filter(Boolean).join(' · ') || null,
      }))
    }
    case 'coalition': {
      const page = await listCoalitions({ search: q || undefined }, 0, 25)
      return page.content.map((c) => ({
        id: c.id,
        label: c.shortName ? `${c.name} (${c.shortName})` : c.name,
        hint: c.statusCode,
      }))
    }
    case 'user': {
      const page = await listUsers({ ...EMPTY_USER_FILTERS, q, status: ['ACTIVE'] }, 0, 25)
      return page.content.map((u) => ({
        id: u.id,
        // Phone, not email: email is optional since the phone-identity change, so it is no
        // longer something every account can be labelled or disambiguated by.
        label: u.name || u.phone,
        hint: u.name ? u.phone : null,
      }))
    }
  }
}

/** Resolve the CURRENT value's label, so an already-saved id does not render as a uuid. */
async function resolveOne(source: Source, id: string): Promise<PickerOption | null> {
  try {
    if (source === 'party') {
      const p = await getParty(id)
      return { id: p.id, label: p.shortName ? `${p.name} (${p.shortName})` : p.name }
    }
    if (source === 'coalition') {
      const c = await getCoalition(id)
      return { id: c.id, label: c.shortName ? `${c.name} (${c.shortName})` : c.name }
    }
    if (source === 'candidate') {
      const c = await getCandidate(id)
      return { id: c.id, label: c.fullName, hint: c.unitName }
    }
    const rows = await listUsers({ ...EMPTY_USER_FILTERS, q: '' }, 0, 200)
    const u = rows.content.find((r) => r.id === id)
    return u ? { id: u.id, label: u.name || u.phone, hint: u.phone } : null
  } catch {
    // A value we cannot resolve still renders — as its id, marked unresolved. Dropping it
    // would silently clear a field the record already holds.
    return null
  }
}

/**
 * The display name for an id the record already holds, for read-only views.
 *
 * The drawer shows `relatedPartyId` and `relatedCandidateId`; rendering a raw uuid there
 * tells the reader nothing and makes the record unreadable in exactly the situation it
 * exists for — somebody asking about this incident months later. Resolving on demand keeps
 * the response DTO free of fields only one panel needs.
 */
export function useEntityLabel(source: Source, id: string | null | undefined): string | null {
  const [name, setName] = useState<string | null>(null)
  // `id` and `source` are listed: a drawer reopened on another incident must re-resolve,
  // not keep showing the previous row's party.
  useEffect(() => {
    if (!id) { setName(null); return }
    let alive = true
    void resolveOne(source, id).then((o) => {
      if (alive) setName(o?.label ?? `${id.slice(0, 8)}… (could not be resolved)`)
    })
    return () => { alive = false }
  }, [source, id])
  return name
}

export function EntityPicker({
  source, value, onChange, placeholder, id,
}: {
  source: Source
  value: string | null
  onChange: (id: string | null) => void
  placeholder?: string
  id?: string
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debounced = useDebounced(term, 300)
  const [options, setOptions] = useState<PickerOption[]>([])
  const [chosen, setChosen] = useState<PickerOption | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  // Resolve whatever the record already holds, so an existing value reads as a name.
  // `value` and `source` are both listed: with `[]` a drawer opened on a second row would
  // keep showing the first row's name.
  useEffect(() => {
    if (!value) { setChosen(null); return }
    if (chosen?.id === value) return
    let alive = true
    void resolveOne(source, value).then((o) => {
      if (alive) setChosen(o ?? { id: value, label: `${value.slice(0, 8)}… (unresolved)` })
    })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, source])

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setOptions(await query(source, debounced))
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setDenied(true)
      else setError(explain(err))
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [source, debounced])

  // Only while open — a closed picker has no business issuing searches.
  useEffect(() => { if (open) void run() }, [open, run])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (denied) {
    return (
      <p className="text-erp-xs text-muted-foreground border-l-2 border-amber-400 pl-2 py-1">
        This list needs <code className="font-mono">{SOURCE_AUTHORITY[source]}</code>, which
        this account does not hold. Ask an administrator for it — the field can be left
        empty in the meantime.
      </p>
    )
  }

  return (
    <div ref={box} className="relative">
      <button type="button" id={id} onClick={() => setOpen((o) => !o)}
        aria-expanded={open} aria-haspopup="listbox"
        className="erp-input h-9 w-full inline-flex items-center gap-2 text-left">
        <span className={cn('truncate flex-1 min-w-0', !chosen && 'text-muted-foreground')}>
          {chosen ? chosen.label : (placeholder ?? 'Search…')}
        </span>
        {chosen && (
          <span role="button" tabIndex={0} aria-label="Clear"
            onClick={(e) => { e.stopPropagation(); onChange(null); setChosen(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(null) } }}
            className="shrink-0 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-60 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto erp-card shadow-lg">
          <div className="flex items-center gap-2 px-3 h-9 border-b border-border sticky top-0 bg-card">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder="Type to search…"
              className="bg-transparent outline-none text-erp-sm w-full min-w-0" />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 opacity-60" />}
          </div>

          {error && <p className="px-3 py-2 text-erp-xs text-red-700 dark:text-red-400">{error}</p>}

          {!loading && options.length === 0 && !error && (
            <p className="px-3 py-3 text-erp-xs text-muted-foreground">
              {term
                ? `Nothing matches “${term}”.`
                : 'Nothing to choose from yet.'}
            </p>
          )}

          <ul role="listbox">
            {options.map((o) => (
              <li key={o.id}>
                <button type="button"
                  onClick={() => { onChange(o.id); setChosen(o); setOpen(false); setTerm('') }}
                  className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-start gap-2">
                  <Check className={cn('h-3.5 w-3.5 mt-0.5 shrink-0',
                    value === o.id ? 'opacity-100 text-chocolate' : 'opacity-0')} />
                  <span className="min-w-0">
                    <span className="block text-erp-sm truncate">{o.label}</span>
                    {o.hint && (
                      <span className="block text-erp-xs text-muted-foreground truncate">{o.hint}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
